/**
 * User Management API Hooks
 * React Query hooks for user CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers } from '@/config/endpoints';
import { DEFAULT_PAGE_SIZE } from '@/config/constants';
import { toast } from '@/hooks/use-toast';
import type {
  ApiUser,
  UserDetailResponse,
  UpdateUserRequest,
  UsersListParams,
  ItemResponse,
  ListPaginated,
} from '@/types/api';

interface UseDeleteUserMutationOptions {
  onSuccessCallback?: () => void;
}
interface UseUpdateUserMutationOptions {
  onSuccessCallback?: () => void;
}

// ============================================================================
// Query Keys
// ============================================================================

export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: (params: UsersListParams) =>
    [...userQueryKeys.lists(), params] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...userQueryKeys.details(), id] as const,
} as const;

// ============================================================================
// Users List Query (Paginated)
// ============================================================================

export function useUsersQuery(params: UsersListParams = {}) {
  return useQuery({
    queryKey: userQueryKeys.list(params),
    queryFn: async (): Promise<ListPaginated<ApiUser>> => {
      const client = new AxiosClient<undefined, ApiUser>({
        endpoint: Endpoints.USERS_LIST,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: params.page || 1,
          page_size: params.page_size || DEFAULT_PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.role && { role: params.role }),
          ...(params.property && { property: params.property }),
          ...(params.for_assignment && { for_assignment: params.for_assignment }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch users');
      }

      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// User Detail Query
// ============================================================================

export function useUserQuery(userId: string | number) {
  return useQuery({
    queryKey: userQueryKeys.detail(userId),
    queryFn: async (): Promise<UserDetailResponse> => {
      const client = new AxiosClient<undefined, UserDetailResponse>({
        endpoint: EndpointHelpers.getUserDetail(userId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch user details');
      }

      return response.data;
    },
    enabled: !!userId, // Only run if userId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Update User Mutation
// ============================================================================

export function useUpdateUserMutation(
  userId: string | number,
  options: UseUpdateUserMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      userData: UpdateUserRequest
    ): Promise<ItemResponse<UserDetailResponse>> => {
      const client = new AxiosClient<UpdateUserRequest, UserDetailResponse>({
        endpoint: EndpointHelpers.updateUser(userId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(userData);

      if (!response.status) {
        // Throw error with full response data preserved
        const error = new Error(response.error || 'Failed to update user');
        (error as any).response = { data: response };
        throw error;
      }

      return response;
    },

    onSuccess: response => {
      toast({
        title: 'User Updated',
        description: `${response.data.name} has been successfully updated.`,
      });

      // Invalidate both list and detail queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(userId) });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update user error:', error);

      let errorMessage = 'Failed to update user. Please try again.';
      let errorTitle = 'Update Failed';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          // Handle validation errors
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else if (typeof errorData.detail === 'string') {
          // Handle string detail messages (e.g., "Cannot change role" errors)
          errorMessage = errorData.detail;
          errorTitle = errorData.error || 'Update Failed';
        } else {
          errorMessage = errorData.error || errorMessage;
        }
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Delete User Mutation
// ============================================================================

export function useDeleteUserMutation(
  options: UseDeleteUserMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      userId: string | number
    ): Promise<ItemResponse<string>> => {
      const client = new AxiosClient<undefined, string>({
        endpoint: EndpointHelpers.deleteUser(userId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        // Throw error with full response data preserved
        const error = new Error(response.error || 'Failed to delete user');
        (error as any).response = { data: response };
        throw error;
      }

      return response;
    },

    onSuccess: (response, userId) => {
      toast({
        title: 'User Deleted',
        description: 'User has been successfully deleted.',
      });

      // Remove from cache and refetch
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
      queryClient.removeQueries({ queryKey: userQueryKeys.detail(userId) });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete user error:', error);

      let errorMessage = 'Failed to delete user. Please try again.';
      let errorTitle = 'Delete Failed';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
          errorTitle = errorData.error || 'Delete Failed';
        } else {
          errorMessage = errorData.error || errorMessage;
        }
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}
